import { Descendant, Editor, Element as SlateElement, Node, Path } from 'slate';
import analytics from '@ublend-npm/aula-analytics';

import { AulaEntities, isAulaEntity } from '../../Entities';
import { insertFilesFromEvent } from '../../lib/insertFilesFromEvent';
import { AulaPlugin } from '../plugins';
import { getAnalyticsEvent } from '../../lib/useAnalytics';
import { getPasteSource } from './getPasteSource';
import {
  AulaEditorContext,
  AulaEditorInstance,
  AulaElement,
} from '../../types';
import { parseMsDocHtmlToSlate } from './parseHtmlToSlate/parseMsDocHtmlToSlate';
import { latexType } from '../latex/latexType';
import { codeType } from '../code/codeType';
import { fetchMeta } from '../../lib/fetchMeta';

const sendAnalyticsEvent = (
  context: AulaEditorContext,
  textHTMLData: string,
) => {
  const analyticsEvent = getAnalyticsEvent('EDITOR_PASTE_CONTENT', context, {
    pasteSource: getPasteSource(textHTMLData),
  });
  analytics.track(analyticsEvent);
};

const getClipboardPlainTextData = (event: React.ClipboardEvent<Element>) => {
  return event.clipboardData.getData('text/plain');
};

const getClipboardHtmlTextData = (event: React.ClipboardEvent<Element>) => {
  return event.clipboardData.getData('text/html');
};

const getClipboardApplicationJsonData = (
  event: React.ClipboardEvent<Element>,
) => {
  return event.clipboardData.getData('application/json');
};

const isClipboardContentFiles = (event: React.ClipboardEvent<Element>) => {
  return (
    !getClipboardHtmlTextData(event) &&
    !getClipboardPlainTextData(event) &&
    event.clipboardData.files.length > 0
  );
};

const getSelectedChild = (editor: AulaEditorInstance) => {
  if (editor.selection && editor.selection.anchor)
    return editor.children[editor.selection.anchor.path[0]];
  return undefined;
};

const isAllowedBlockForHtml = (selected: AulaElement) => {
  return !(selected.type === codeType || selected.type === latexType);
};

const createBlockLinkEntityData = (
  child: Descendant,
  context: AulaEditorContext,
) => {
  if (!SlateElement.isElement(child)) return;

  const { type, url } = child;

  if (type !== 'blockLink' || !url) return;

  const entity = context.createEntity({
    type: 'meta',
    url: decodeURIComponent(url),
  });

  void fetchMeta(url, context.config.apiUrl)
    .then((data) => {
      context.updateEntity(entity, {
        data,
      });
    })
    .catch(() => {
      context.updateEntity(entity, {
        data: {
          title: url,
        },
      });
    });

  child.entity = entity;
  return;
};

const insertPlainText = (
  event: React.ClipboardEvent<Element>,
  editor: AulaEditorInstance,
) => {
  const textContent = getClipboardPlainTextData(event);

  // The match naively tries to find paragraphs split by a hard break, while
  // ignoring soft breaks within a paragraph. Areas with multiple new lines
  // will also be greedily consumed down to a single break, preventing weird
  // spacing issues for the end user, at the cost of breaking any weird content
  // relying on multiple empty lines.
  textContent.split(/\n\n+/g).forEach((textLine, index) => {
    // Try to figure out if the previous node is empty, and add the contents of the
    // first paragraph to it, for the first paragraph being pasted.
    //
    // This prevents users from always seeing an empty line when pasting into an
    // empty editor, which by default contains an empty text node at the start.
    if (index === 0 && editor.selection) {
      const selectedNodePath = Path.parent(editor.selection.anchor.path);
      const selectedNode = Node.get(editor, selectedNodePath);

      if (!Editor.isVoid(editor, selectedNode)) {
        return Editor.insertText(editor, textLine);
      }
    }

    // If the text line/paragraph can't be merged with a previous empty node, insert it
    // as an entirely new node:
    return Editor.insertNode(editor, {
      children: [{ text: textLine }],
    });
  });

  event.preventDefault();
};

const insertAsAulaData = (pastedData: string, context: AulaEditorContext) => {
  const entitiesData: AulaEntities = JSON.parse(pastedData);
  Object.entries(entitiesData).forEach(([entityId, entity]) => {
    if (isAulaEntity(entity)) {
      context.updateEntity(entityId, entity);
    }
  });
};

const insertAsMsDocHtml = (
  event: React.ClipboardEvent<Element>,
  editor: AulaEditorInstance,
  context: AulaEditorContext,
  parsedHtml: Document,
) => {
  event.preventDefault();

  try {
    const fragment = parseMsDocHtmlToSlate(parsedHtml.body);

    fragment.forEach((child) => createBlockLinkEntityData(child, context));

    editor.insertFragment(fragment);
  } catch (error) {
    console.log(error);
    insertPlainText(event, editor);
  }
};

const handleOnPaste = (
  event: React.ClipboardEvent<HTMLDivElement>,
  editor: AulaEditorInstance,
  context: AulaEditorContext,
) => {
  const pastedData = getClipboardApplicationJsonData(event);
  const textHTMLData = getClipboardHtmlTextData(event);

  sendAnalyticsEvent(context, textHTMLData);

  const selected = getSelectedChild(editor);

  if (selected && isAllowedBlockForHtml(selected as AulaElement)) {
    if (textHTMLData) {
      const parsed = new DOMParser().parseFromString(textHTMLData, 'text/html');

      if (getPasteSource(textHTMLData) === 'Microsoft Word') {
        insertAsMsDocHtml(event, editor, context, parsed);
        return;
      }
    }

    if (pastedData) {
      insertAsAulaData(pastedData, context);
      return;
    }
  }

  if (getClipboardPlainTextData(event)) {
    insertPlainText(event, editor);
    return;
  }

  /**
   * When copying from word, word adds files to the clipboard event
   * as well as text, html and rtf 😅
   * therefore we need this condition to be explicit
   */
  if (isClipboardContentFiles(event)) {
    insertFilesFromEvent(editor, event, context, editor.selection);
    return;
  }
};

export const handlePaste: AulaPlugin = (editableProps, editor, context) => {
  if (context == null) throw new Error('context must be defined');

  return {
    ...editableProps,
    onPaste: (event) => handleOnPaste(event, editor, context),
  };
};
